import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AlertTriangle,
  Bell,
  Building2,
  CheckCircle2,
  Download,
  FileText,
  FileWarning,
  Info,
  Send,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  AVISO_DE_EXPORTACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarCentavos, paraCentavos } from '@/nucleo/dinheiro';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageBanner } from '@/ui/PageBanner';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { StatusBadge } from '@/ui/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import { usePersonaFechamento } from '../fechamento-v2/persona';
import { useDres } from '../hooks/useDres';
import {
  APRESENTACAO_DO_STATUS,
  ROTULO_DO_DOCUMENTO,
  competenciaPorExtenso,
  normalizar,
} from './apresentacao';
import { CicloMensalAbas } from './CicloMensalAbas';
import { useMedicoesSme } from './dados';
import { STATUS_COM_DEMONSTRATIVO, type MedicaoSme } from './tipos';

/**
 * Demonstrativos (SME) — `/sme/demonstrativos`, aba do "Ciclo mensal".
 *
 * Migrada do protótipo (`SmeDemonstrativos.tsx`): o demonstrativo formal de
 * cada medição a partir de `CALCULADA`, com os quatro indicadores, busca, DRE,
 * tipo, status e competência. Escolher uma linha abre, acima da lista, o
 * painel do demonstrativo: alerta fiscal, documentos pendentes para a
 * liquidação e as três ações (PDF, reenviar ao credenciado, cobrar documentos).
 * `?medicao=` na URL abre o painel direto — é assim que a aba Medições chega.
 *
 * A LEITURA É MOCKADA E NENHUMA AÇÃO GRAVA. Não há módulo em
 * `apps/api/src/modulos/` que sirva medições à SME, gere o PDF do
 * demonstrativo ou notifique o credenciado. O dado vem de `dados.ts` sob a
 * exceção registrada em `CLAUDE.md`; as três ações mostram o aviso de
 * indisponibilidade (padrão de `SlaAprovacaoSmePage`), e o painel traz um aviso
 * fixo de que nada é enviado — aviso que some deixa quem audita uma captura de
 * tela concluir que o credenciado foi cobrado.
 *
 * DIVERGÊNCIAS CONSCIENTES EM RELAÇÃO AO PROTÓTIPO:
 *
 * 1. **Sem coluna "Ações".** O protótipo termina a linha num botão
 *    "Detalhar"/"Selecionado". CLAUDE.md, regras de interface, item 9: a linha
 *    inteira é o alvo; a escolhida fica destacada.
 * 2. **O filtro de DRE fica sempre visível** — mesma decisão de `/sme/revisoes`.
 * 3. **A persona DRE não recebe a lista.** No protótipo, quem é de DRE vê só os
 *    demonstrativos da própria diretoria, recortados pelo banco. Aqui não há
 *    como saber de qual diretoria ela é (`GET /eu` não tem campo de DRE), e
 *    servir os de todas seria mostrar o que ela nunca verá — mesma regra do
 *    Painel e das Tratativas (CLAUDE.md, portal SME).
 * 4. **Medição sem demonstrativo avisa.** Vinda da aba Medições com uma medição
 *    que ainda não foi calculada, o protótipo não mostra nada; aqui a tela diz
 *    por que o painel não abriu.
 */

const FILTROS_PADRAO = {
  d_busca: '',
  d_mes: '',
  d_dre: '',
  d_tipo: '',
  d_status: '',
};

const OPCOES_DE_STATUS = STATUS_COM_DEMONSTRATIVO.map((status) => ({
  value: status,
  label: APRESENTACAO_DO_STATUS[status].rotulo,
}));

const OPCOES_DE_TIPO = [
  { value: 'PF', label: 'Pessoa Física' },
  { value: 'PJ', label: 'Pessoa Jurídica' },
];

export const TITULO_SEM_RECORTE = 'Os demonstrativos da sua diretoria ainda não estão disponíveis';

const DESCRICAO_SEM_RECORTE =
  'Esta lista mostra os demonstrativos de todas as diretorias, e o sistema ainda não sabe de ' +
  'qual diretoria é quem está olhando: o recorte depende de uma rota que ainda não existe. ' +
  'Até lá, ela aparece apenas para a SME.';

function somar(medicoes: readonly MedicaoSme[]): string {
  return formatarCentavos(medicoes.reduce((s, m) => s + (paraCentavos(m.valorTotal) ?? 0), 0));
}

export function DemonstrativosSmePage() {
  const persona = usePersonaFechamento();
  const [parametros, definirParametros] = useSearchParams();
  const selecionadaId = parametros.get('medicao') ?? '';
  const { values, setFilter, clearAll } = useUrlFilters(FILTROS_PADRAO);
  const { data: dres = [] } = useDres();
  const consulta = useMedicoesSme();

  const todas = useMemo(() => consulta.data ?? [], [consulta.data]);

  const demonstrativos = useMemo(() => {
    const alvo = normalizar(values.d_busca.trim());
    return todas.filter((m) => {
      if (!STATUS_COM_DEMONSTRATIVO.includes(m.status)) return false;
      if (values.d_mes && m.mesReferencia !== values.d_mes) return false;
      if (values.d_dre && m.dre.id !== values.d_dre) return false;
      if (values.d_tipo && m.credenciado.tipo !== values.d_tipo) return false;
      if (values.d_status && m.status !== values.d_status) return false;
      if (
        alvo &&
        !normalizar(m.credenciado.nome).includes(alvo) &&
        !normalizar(m.credenciado.cpfCnpj).includes(alvo)
      ) {
        return false;
      }
      return true;
    });
  }, [todas, values]);

  const cabecalho = (
    <>
      <PageHeader
        title="Demonstrativos"
        description="Gere PDFs, cobre documentos pendentes do credenciado e acompanhe a defasagem fiscal por competência."
      />
      <CicloMensalAbas />
    </>
  );

  if (persona === 'DRE') {
    return (
      <div className="space-y-6">
        {cabecalho}
        <EmptyState
          icon={Building2}
          title={TITULO_SEM_RECORTE}
          description={DESCRICAO_SEM_RECORTE}
        />
      </div>
    );
  }

  const selecionada = demonstrativos.find((m) => m.id === selecionadaId) ?? null;
  const pedidaSemDemonstrativo =
    selecionadaId !== '' &&
    todas.some((m) => m.id === selecionadaId && !STATUS_COM_DEMONSTRATIVO.includes(m.status));

  function selecionar(medicao: MedicaoSme) {
    definirParametros(
      (anteriores) => {
        const proximos = new URLSearchParams(anteriores);
        proximos.set('medicao', medicao.id);
        return proximos;
      },
      { replace: true },
    );
  }

  const indicadores: StatItem[] = [
    { title: 'Demonstrativos', value: demonstrativos.length, icon: FileText },
    {
      title: 'Aprovados',
      value: demonstrativos.filter((m) => m.status === 'APROVADA').length,
      icon: CheckCircle2,
    },
    {
      title: 'Liquidados / Pagos',
      value: demonstrativos.filter((m) => m.status === 'LIQUIDADA' || m.status === 'PAGA').length,
      icon: CheckCircle2,
    },
    { title: 'Valor total', value: somar(demonstrativos), icon: FileText },
  ];

  const campos: FilterFieldConfig[] = [
    {
      key: 'd_busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Credenciado ou CPF/CNPJ...',
      flex: true,
    },
    {
      key: 'd_dre',
      label: 'DRE',
      type: 'select',
      options: dres.map((d) => ({ value: d.id, label: `${d.codigo} — ${d.nome}` })),
      allOptionLabel: 'Todas as DREs',
      width: 'w-full sm:w-[220px]',
    },
    {
      key: 'd_tipo',
      label: 'Tipo',
      type: 'select',
      options: OPCOES_DE_TIPO,
      allOptionLabel: 'Todos',
      width: 'w-full sm:w-[160px]',
    },
    {
      key: 'd_status',
      label: 'Status',
      type: 'select',
      options: OPCOES_DE_STATUS,
      allOptionLabel: 'Todos',
      width: 'w-full sm:w-[180px]',
    },
  ];

  const colunas: DataTableColumn<MedicaoSme>[] = [
    {
      header: 'Credenciado',
      sortKey: (m) => m.credenciado.nome,
      cell: (m) => (
        <div className="flex flex-col">
          <span className="font-medium">{m.credenciado.nome}</span>
          <span className="font-mono text-xs text-muted-foreground">{m.credenciado.cpfCnpj}</span>
        </div>
      ),
    },
    {
      header: 'DRE',
      sortKey: (m) => m.dre.codigo,
      cell: (m) => <span className="text-sm font-medium">{m.dre.codigo}</span>,
    },
    {
      header: 'Competência',
      sortKey: (m) => m.mesReferencia,
      cell: (m) => competenciaPorExtenso(m.mesReferencia),
    },
    { header: 'Tipo', sortKey: (m) => m.credenciado.tipo, cell: (m) => m.credenciado.tipo },
    {
      header: 'Valor',
      sortKey: (m) => paraCentavos(m.valorTotal),
      cell: (m) => <MoneyDisplay value={m.valorTotal} />,
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
    },
    {
      header: 'Status',
      sortKey: (m) => APRESENTACAO_DO_STATUS[m.status].rotulo,
      cell: (m) => (
        <StatusBadge
          status={APRESENTACAO_DO_STATUS[m.status].variante}
          label={APRESENTACAO_DO_STATUS[m.status].rotulo}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {cabecalho}

      <StatsGrid stats={indicadores} />

      {pedidaSemDemonstrativo && (
        <PageBanner
          tone="info"
          icon={Info}
          title="Esta medição ainda não tem demonstrativo"
          description="O demonstrativo nasce quando a medição é calculada. Acompanhe o andamento dela na aba Medições."
        />
      )}

      {selecionada && <PainelDoDemonstrativo medicao={selecionada} />}

      <FilterBar
        fields={campos}
        values={values}
        defaultValues={FILTROS_PADRAO}
        onChange={setFilter}
        onClear={clearAll}
      >
        <MonthFilter
          id="d-competencia"
          value={values.d_mes}
          onChange={(mes) => setFilter('d_mes', mes)}
        />
      </FilterBar>

      {consulta.isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void consulta.refetch() }}
        />
      ) : (
        <DataTable
          columns={colunas}
          data={demonstrativos}
          isLoading={consulta.isLoading}
          onRowClick={selecionar}
          getRowClassName={(m) => (m.id === selecionadaId ? 'bg-primary/5' : undefined)}
          emptyIcon={FileText}
          emptyTitle="Nenhum demonstrativo disponível"
          emptyDescription="Apenas medições calculadas, aprovadas ou liquidadas aparecem aqui."
          ariaLabel="Demonstrativos por credenciado e competência"
        />
      )}
    </div>
  );
}

/**
 * O painel do demonstrativo escolhido — o `DetalhePanel` do protótipo.
 *
 * As três ações aparecem como lá, mas nenhuma conclui: não há rota que gere o
 * PDF, reenvie ou cobre. Cada uma mostra o aviso de indisponibilidade, e o
 * aviso fixo no topo do painel diz o mesmo para quem só vê a tela.
 */
function PainelDoDemonstrativo({ medicao }: { medicao: MedicaoSme }) {
  const pendentes = medicao.documentosPendentes;
  const alerta = medicao.alertaFiscal;

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base">{medicao.credenciado.nome}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {competenciaPorExtenso(medicao.mesReferencia)} · {medicao.credenciado.tipo} · DRE{' '}
              {medicao.dre.codigo} · Termo {medicao.termoSei ?? '—'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => mostrarAviso(AVISO_DE_EXPORTACAO_INDISPONIVEL)}
            >
              <Download className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
            >
              <Send className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Reenviar ao credenciado
            </Button>
            <ConfirmDialog
              title="Cobrar documentos do credenciado?"
              description={
                pendentes.length > 0
                  ? `O credenciado seria notificado para enviar ${pendentes.length} documento(s) pendente(s). A cobrança ainda não é enviada: nada é registrado.`
                  : 'Não há documentos pendentes.'
              }
              confirmLabel="Enviar cobrança"
              onConfirm={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
              disabled={pendentes.length === 0}
              trigger={
                <Button size="sm" disabled={pendentes.length === 0}>
                  <Bell className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Cobrar documentos
                </Button>
              }
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>As ações deste demonstrativo ainda não funcionam</AlertTitle>
          <AlertDescription>
            Nenhum PDF é gerado e nada é reenviado ou cobrado do credenciado. Estas ações entram em
            funcionamento junto com o restante do ciclo de medição.
          </AlertDescription>
        </Alert>

        {/* `Alert`, e não `PageBanner`: este se muda para o espaço de avisos do
            cabeçalho da página, e o alerta é deste demonstrativo, não da tela. */}
        {alerta && (
          <Alert
            variant={alerta.nivel === 'CRITICO' ? 'destructive' : 'default'}
            className={alerta.nivel === 'ATENCAO' ? 'border-warning/50 bg-warning/15' : undefined}
          >
            <AlertTriangle
              className={alerta.nivel === 'ATENCAO' ? 'h-4 w-4 text-warning' : 'h-4 w-4'}
              aria-hidden="true"
            />
            <AlertTitle>
              {alerta.nivel === 'CRITICO' ? 'Risco fiscal crítico' : 'Atenção à competência'}
            </AlertTitle>
            <AlertDescription>{alerta.mensagem}</AlertDescription>
          </Alert>
        )}

        <div>
          <h3 className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <FileWarning className="h-3.5 w-3.5" aria-hidden="true" /> Documentos pendentes para
            liquidação
          </h3>
          {pendentes.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Nenhum documento obrigatório pendente.
            </p>
          ) : (
            <ul className="flex list-none flex-wrap gap-2 p-0">
              {pendentes.map((tipo) => (
                <li key={tipo}>
                  <Badge variant="outline" className={STATUS_TONE.warning}>
                    {ROTULO_DO_DOCUMENTO[tipo]}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default DemonstrativosSmePage;
